import { omit } from 'lodash';
import { Moment } from 'moment';
import { DateType } from 'api/models';
import {
  CreateProjectPayload,
  PlaceholderAssignmentMap,
  ProjectCreateLinkedResource,
  ProjectSourceType,
} from 'api/project';
import { Field } from 'api/tasks';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { FixedFields } from 'components/organisms/projects/project-create/fixed-fields-render';
import { toProjectBulkMergePayload } from 'components/organisms/projects/project-plan/import-from-template/utils';
import {
  FieldType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { nonDefaultField } from 'utils/project-fields/use-project-fields';
import { unreachable } from 'utils/unreachable';
import { ProjectData } from './types';

/**
 * Util function to convert form values {[fieldId]: fieldValue} in to API [{fieldId: number, fieldValue: any}, ...]
 * @param values
 * @param fields
 */
export const toFieldsValues = (
  values: Record<string, any>,
  fields: Field[],
) => {
  const fieldIdsThatHasValue = Object.keys(values).filter(
    (key) => values[key] != null,
  );
  const constructValueBasedOnFieldType = (field: Field, value: any) => {
    const type = filterableFieldType(field);

    switch (type) {
      case FieldType.SingleUser:
      case FieldType.SingleSelect:
        return value.value;
      case FieldType.MultiUser:
      case FieldType.MultiSelect:
        return value.map((v) => v.value);
      case FieldType.Date:
        return value.format(API_DATE_FORMAT);
      case FieldType.Boolean:
        return value;
      case FieldType.Text:
      case FieldType.MultiLineText:
        return value;
      case FieldType.Number:
        return Number(value);
      case FieldType.Assignee:
        return value;
      case FieldType.Estimate:
        // estimate is not an actual custom field type and this case is not possible. In future if it is needed, handle it accordingly
        return unreachable(type as never);
      default:
        return unreachable(type);
    }
  };

  const fieldIds = fieldIdsThatHasValue.filter((id) =>
    fields.find(({ fieldId }) => fieldId === Number(id)),
  );

  return fieldIds.map((id) => {
    const fieldId = Number(id);

    const field = fields.find((f) => f.fieldId === fieldId)!;

    return {
      fieldId,
      fieldValue: field
        ? constructValueBasedOnFieldType(field, values[fieldId])
        : null,
    };
  });
};
export const isPartnerCompanyValue = (value: string | number): boolean =>
  value.toString().indexOf('partner-') !== -1;
export const companyIdFromPartnerCompanyValue = (value: string): number =>
  parseInt(value.split('partner-')[1], 10);

/**
 * Method to construct create project API payload
 * @param customer
 * @param projectName
 * @param visibility
 * @param projectOwner
 * @param customerChampion
 * @param dueDate
 * @param startDate
 * @param externalUsers
 * @param internalUsers
 * @param fields
 * @param status
 * @param source
 * @param projectFields
 */
export const createProjectPayload = (
  {
    customer,
    projectOwner,
    customerChampion,
    dueDate,
    startDate,
    externalUsers,
    internalUsers,
    fields,
    status,
    source,
    jiraEpicMapping,
    sources,
    assignToProjectOwner,
    externalPlaceholders,
    internalPlaceholders,
    partners,
    ...rest
  }: ProjectData,
  projectFields: Field[],
): CreateProjectPayload => {
  const toUser = (option) => ({
    userId: option.value,
  });
  const toDate = (date?: Moment): DateType | undefined => {
    return date ? date.format(API_DATE_FORMAT) : undefined;
  };
  const toCompany = (companyOption) => {
    return { companyId: companyOption.value };
  };

  let projectStatus = {};

  if (status) {
    const statusFieldId = status ? Object.keys(status)[0] : null;

    if (statusFieldId) {
      projectStatus = {
        [statusFieldId]: status[statusFieldId],
      };
    }
  }

  const fieldValues = toFieldsValues(
    { ...fields, ...projectStatus },
    projectFields,
  );

  const linkedResources = jiraEpicMapping
    ? [omit(jiraEpicMapping, ['value', 'label']) as ProjectCreateLinkedResource]
    : [];

  const placeholders = [
    ...(internalPlaceholders || []),
    ...(externalPlaceholders || []),
  ];

  const teamToUserMapRequests: PlaceholderAssignmentMap[] = [];

  const assignedPlaceholders = placeholders.filter(
    (_placeholder) => _placeholder.user,
  );

  assignedPlaceholders.forEach((_placeholder) => {
    const value = _placeholder?.user?.value.toString();
    const isPartnerCompanyAssigned = isPartnerCompanyValue(value ?? '');
    const userId = isPartnerCompanyAssigned
      ? undefined
      : (_placeholder?.user?.value as number);
    const companyId = isPartnerCompanyAssigned
      ? companyIdFromPartnerCompanyValue(value ?? '')
      : undefined;

    teamToUserMapRequests.push({
      teamId: _placeholder.teamId,
      userId,
      companyId,
      roleId: _placeholder.roleId,
    });

    if (!_placeholder.duplicates) {
      return;
    }

    Object.values(_placeholder.duplicates).forEach((_duplicate) => {
      teamToUserMapRequests.push({
        teamId: _duplicate.teamId,
        userId,
        companyId,
        roleId: _duplicate.roleId,
      });
    });
  });

  const replacedUserIds =
    teamToUserMapRequests?.map(({ userId }) => userId) ?? [];

  const uniqueTeamMembers = internalUsers?.filter(
    ({ value: userId }) => !replacedUserIds.includes(Number(userId)),
  );

  return {
    ...rest,
    customer: toCompany(customer),
    customerChampion: customerChampion && toUser(customerChampion),
    projectOwner: toUser(projectOwner),
    startDate: toDate(startDate),
    dueDate: toDate(dueDate),
    teamMembers: [...(externalUsers ?? []), ...(uniqueTeamMembers ?? [])].map(
      toUser,
    ),
    source: source && {
      sourceId: source.value,
      sourceType: ProjectSourceType.Template,
    },
    sources: toProjectBulkMergePayload({ sources }),
    fields: fieldValues,
    partners: (partners ?? [])?.map(toCompany),
    linkedResources,
    ...(assignToProjectOwner && { defaultAssignee: [projectOwner?.value] }),
    ...(teamToUserMapRequests.length > 0 && { teamToUserMapRequests }),
  };
};

const customFieldsFilter = (field: Field) => {
  const hasSpecificFields = [FixedFields.ARR, FixedFields.ProjectFee].includes(
    field.fieldName,
  );

  if (hasSpecificFields) {
    return hasSpecificFields;
  }

  return nonDefaultField(field);
};

const ratingFieldsFilter = (field: Field) => field.fieldType !== 'RATING';

export const fieldsToBeDisplayedInCreateProject = (f: Field) =>
  customFieldsFilter(f) && ratingFieldsFilter(f);
